import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { PartyPopper, Zap, Check, ArrowRight, Mail, Users, Monitor, ShieldCheck, Sparkles, ShoppingCart, Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import FestivalBasketPanel, { useBasket } from "@/components/FestivalBasketPanel";
import { ReplaceTierDialog } from "@/components/ReplaceTierDialog";
import { HELPER_ROLE_INFO, RoleMedia } from "@/components/HelperRoleCard";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Iter303 — Public Festival Pass landing page.
 *
 * Replaces the older /festival marketing page. Shows four tier cards
 * (Starter / Standard / Pro / Unlimited) with prices or POA, seat
 * counts and included features. Anyone can view without logging in.
 * The "Get Started" CTA routes to the Festival Organiser signup path
 * (`/register?as=organiser`). Logged-in coaches see an "Enable Festival
 * Organiser tools" button instead — hits POST /auth/me/enable-organiser.
 */

const TIER_ORDER = ["starter", "standard", "pro", "unlimited"];
const TIER_META = {
  starter: {
    label: "Starter",
    tagline: "Up to 50 players",
    accent: "#CCFF00",
    self_service: false,
    self_service_bolton: true,
  },
  standard: {
    label: "Standard",
    tagline: "Up to 180 players",
    accent: "#66C2FF",
    self_service: false,
    self_service_bolton: true,
  },
  pro: {
    label: "Pro",
    tagline: "Up to 500 players",
    accent: "#FFB020",
    self_service: true,
    self_service_bolton: false,
    highlight: true,
  },
  unlimited: {
    label: "Unlimited",
    tagline: "500+ players",
    accent: "#B26BFF",
    self_service: true,
    self_service_bolton: false,
    manual_only: true,
  },
};

function money(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "gbp").toUpperCase(), maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

const INCLUDED_FIELD = { helper_seat: "helper_seats", ops_seat: "ops_seats", deputy_seat: "deputy_seats", roster_ops_seat: "ops_assist" };

export default function FestivalLanding() {
  const { user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const [showAddTier, setShowAddTier] = useState(false);
  const isCoachOrAdmin = user && (user.role === "coach" || user.role === "admin");
  const isAdmin = user && (user.role === "admin" || (user.permissions || []).some((p) => p === "admin" || p === "super_admin"));
  const alreadyOrganiser = !!user?.is_festival_organiser;
  const basket = useBasket(pricing);
  const [pendingTier, setPendingTier] = useState(null);
  const currentTierKey = basket.items.find((i) => pricing?.[i.pass_type] && pricing[i.pass_type].tier !== "bolton")?.pass_type;
  const selectedTier = currentTierKey ? pricing[currentTierKey] : null;
  const addTier = (passKey) => {
    const label = pricing?.[passKey]?.label || passKey;
    const res = basket.addItem(passKey);
    if (res === "conflict") setPendingTier(passKey);
    else if (res === "exists") toast(`${label} is already in your basket`);
    else toast.success(`${label} added to basket`);
  };
  const navigate = useNavigate();
  const location = useLocation();
  // Iter467 · Fix 1 — When a logged-out visitor clicks the basket, send
  // them through the existing register flow with a `next=` back to the
  // landing page carrying `?open_basket=1`. Items persist via the
  // existing `rl_festival_basket_v1` localStorage key, so they re-appear
  // after sign-in. We also set a localStorage flag so the panel re-opens
  // even if the user signs in via a different route (e.g. the login
  // link inside the register page).
  const openBasketOrAuth = () => {
    if (user) {
      setBasketOpen(true);
      return;
    }
    try { window.localStorage.setItem("rl_festival_open_basket_after_auth", "1"); } catch {}
    navigate(`/register?as=organiser&next=${encodeURIComponent("/festival?open_basket=1")}`);
  };
  // Iter467 · Fix 1 — If we come back from auth with either the URL
  // flag or the localStorage flag, pop the basket open and tidy up.
  useEffect(() => {
    if (!user) return;
    let shouldOpen = false;
    try {
      const sp = new URLSearchParams(location.search);
      if (sp.get("open_basket") === "1") shouldOpen = true;
    } catch {}
    try {
      if (window.localStorage.getItem("rl_festival_open_basket_after_auth") === "1") shouldOpen = true;
    } catch {}
    if (shouldOpen) {
      setBasketOpen(true);
      try { window.localStorage.removeItem("rl_festival_open_basket_after_auth"); } catch {}
    }
  }, [user, location.search]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/festival/pricing`, { withCredentials: false })
      .then((r) => setPricing(r.data.pricing))
      .catch(() => setPricing(null));
  }, []);

  const tierGroups = useMemo(() => {
    if (!pricing) return null;
    const groups = {};
    for (const tier of TIER_ORDER) {
      groups[tier] = {
        pass_1day: pricing[`${tier}_1day`],
        pass_2day: pricing[`${tier}_2day`],
        pass_3day: pricing[`${tier}_3day`],
      };
    }
    return groups;
  }, [pricing]);

  const boltons = useMemo(() => {
    if (!pricing) return [];
    return [
      pricing.bolton_helper_seat,
      // Iter467 — OPS Assist missing from the public bolt-on grid.
      pricing.bolton_roster_ops_seat,
      pricing.bolton_ops_seat,
      pricing.bolton_deputy_seat,
      // Iter466 — Self-service is now priced per day count (one bolt-on
      // row per starter_*day / standard_*day combination).
      pricing.bolton_self_service_starter_1day,
      pricing.bolton_self_service_starter_2day,
      pricing.bolton_self_service_starter_3day,
      pricing.bolton_self_service_standard_1day,
      pricing.bolton_self_service_standard_2day,
      pricing.bolton_self_service_standard_3day,
    ].filter(Boolean);
  }, [pricing]);

  const enableOrganiser = async () => {
    setEnabling(true);
    try {
      await api.post("/auth/me/enable-organiser");
      toast.success("Festival Organiser tools enabled. Refreshing…");
      // Small delay so toast is seen, then reload so AuthContext refetches /auth/me.
      setTimeout(() => window.location.assign("/festival/mine"), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enable organiser tools");
    } finally {
      setEnabling(false);
    }
  };

  const primaryHref = alreadyOrganiser ? "/festival/mine" : (user ? "/festival/mine" : "/register?as=organiser");
  const primaryLabel = alreadyOrganiser ? "My festivals →" : (user ? "Enter festival area →" : "Get started");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="festival-landing">
      {/* NAV */}
      <header className="border-b border-[#141414]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          {/* Iter470 · Fix 2 — Brand mark is now a non-interactive logo.
              Previously it was a `<Link to="/">` which doubled as an
              internal navigation shortcut; the public landing page
              shouldn't offer those. */}
          <div className="flex items-center gap-2" data-testid="landing-brand-mark">
            <PartyPopper className="w-6 h-6 text-[#CCFF00]" />
            <div className="label-mono text-[#CCFF00]">RALLY-LAB · FESTIVAL LIVE OPS</div>
          </div>
          <div className="flex items-center gap-2">
            {/* Iter306 — Basket button (always visible). Shows a count
                badge when items are present. */}
            <button
              type="button"
              onClick={openBasketOrAuth}
              data-testid="landing-basket-btn"
              className="relative px-3 py-2 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1"
            >
              <ShoppingCart className="w-4 h-4" />
              Basket
              {basket.items.length > 0 && (
                <span className="ml-1 min-w-[18px] px-1 h-[18px] text-[10px] font-bold rounded-full bg-[#CCFF00] text-black inline-flex items-center justify-center" data-testid="basket-count">
                  {basket.items.length}
                </span>
              )}
            </button>
            {user ? (
              <Link to="/festival/mine" data-testid="landing-nav-mine" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black">
                My festivals →
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="landing-nav-login" className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Log in</Link>
                <Link to="/register?as=organiser" data-testid="landing-nav-signup" className="px-4 py-2 text-xs uppercase tracking-wider font-bold" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#CCFF00] text-[#CCFF00] text-[10px] uppercase tracking-widest mb-6">
          <Zap className="w-3 h-3" /> Four tiers · pay per event · no per-user fees
        </div>
        <h1 className="h-display text-4xl sm:text-6xl lg:text-6xl leading-tight tracking-tighter">
          Run your pickleball festival on <span className="text-[#CCFF00]">one screen</span>.
        </h1>
        <p className="text-base sm:text-lg text-[#A3A3A3] mt-6 max-w-2xl mx-auto leading-relaxed">
          Live court engine, big-screen public viewer, player self-service and helper seats — pick the tier that fits your event size.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
          {isCoachOrAdmin && !alreadyOrganiser ? (
            <button
              type="button"
              onClick={enableOrganiser}
              disabled={enabling}
              data-testid="landing-enable-organiser"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider font-bold disabled:opacity-50"
              style={{ background: "#CCFF00", color: "#0A0A0A" }}
            >
              <Sparkles className="w-4 h-4" /> {enabling ? "Enabling…" : "Enable Festival Organiser tools"}
            </button>
          ) : (
            <Link
              to={primaryHref}
              data-testid="landing-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider font-bold"
              style={{ background: "#CCFF00", color: "#0A0A0A" }}
            >
              <Zap className="w-4 h-4" /> {primaryLabel}
            </Link>
          )}
          <a
            href="#tiers"
            data-testid="landing-secondary-cta"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-wider border border-[#525252] hover:border-white"
          >
            Compare tiers ↓
          </a>
        </div>
      </section>

      {/* TIERS */}
      <section id="tiers" className="max-w-6xl mx-auto px-4 sm:px-6 pb-16" data-testid="landing-tiers">
        <div className="text-center mb-10">
          <div className="label-mono text-[#CCFF00] mb-2">FESTIVAL PASS TIERS</div>
          <h2 className="h-display text-3xl sm:text-4xl">Four tiers. Your duration.</h2>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-lg mx-auto">
            Prices marked <span className="text-white font-semibold">POA</span> are Price-On-Application — get in touch and we'll build a quote.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {tierGroups && TIER_ORDER.map((tier) => (
            <TierCard
              key={tier}
              tier={tier}
              meta={TIER_META[tier]}
              passes={tierGroups[tier]}
              onAddToBasket={addTier}
            />
          ))}
        </div>

        {/* Iter306 — Admin-only tier management. Non-owners never see this. */}
        {isAdmin && (
          <div className="mt-8 border border-dashed border-[#525252] p-5" data-testid="landing-admin-tiers">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-mono text-[#FFB020]">ADMIN · CUSTOM TIERS</div>
                <div className="text-xs text-[#A3A3A3]">Add bespoke packages here — same shape as the built-in tiers.</div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddTier(true)}
                data-testid="admin-add-tier-btn"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
              >
                <Plus className="w-3 h-3" /> Add tier
              </button>
            </div>
            <CustomTierList pricing={pricing} onDeleted={(p) => setPricing(p)} />
          </div>
        )}
      </section>

      {/* BOLT-ONS */}
      {boltons.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-16" data-testid="landing-boltons">
          <div className="text-center mb-6">
            <div className="label-mono text-[#CCFF00] mb-2">BOLT-ONS</div>
            <h3 className="h-display text-2xl">Top up any tier</h3>
            <p className="text-xs text-[#525252] mt-2">Prices are placeholders — final pricing set post-launch.</p>
            {!selectedTier && (
              <p className="text-[11px] uppercase tracking-widest text-[#FFB020] mt-2" data-testid="landing-boltons-select-pass-note">Select a festival pass first</p>
            )}
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {boltons.filter((b) => {
              if (b.kind !== "self_service" || !selectedTier) return true;
              if (Number(b.for_days) !== Number(selectedTier.days)) return false;
              return selectedTier.self_service_included || b.for_tier === selectedTier.tier;
            }).map((b) => {
              const key = Object.entries(pricing).find(([, def]) => def === b)?.[0];
              const canBuy = !b.poa && Number(b.amount) > 0;
              // Iter466 — Surface "3 for £X" bulk pricing when the entry
              // carries `bulk_qty`/`bulk_amount`.
              const bulkQty = Number(b.bulk_qty) || 0;
              const bulkAmount = Number(b.bulk_amount) || 0;
              const ssIncluded = b.kind === "self_service" && !!selectedTier?.self_service_included;
              const locked = !selectedTier || ssIncluded;
              const includedQty = Number(selectedTier?.[INCLUDED_FIELD[b.kind]] || 0);
              const role = HELPER_ROLE_INFO[b.kind];
              return (
                <div key={b.label} className={`card-surface p-4 flex flex-col gap-3 transition-opacity ${locked ? "opacity-40" : ""}`} data-testid={`landing-bolton-${b.kind || "x"}`}>
                <div className="flex items-start justify-between gap-4">
                  {role && <RoleMedia info={role} testId={`landing-role-media-${b.kind === "self_service" ? key : b.kind}`} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{role && !role.useLabel ? role.title : b.label}</div>
                    {b.for_tier && <div className="text-[10px] uppercase tracking-widest text-[#525252]">For {b.for_tier}{b.for_days ? ` · ${b.for_days}-day` : ""}</div>}
                    {ssIncluded && <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] mt-0.5" data-testid={`landing-bolton-included-${key}`}>Included in your tier</div>}
                    {includedQty > 0 && <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-0.5" data-testid={`landing-bolton-included-${key}`}>{includedQty} already included in your tier</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-[#CCFF00]">
                      {canBuy ? money(b.amount, b.currency) : <span className="text-[#525252]">TBC</span>}
                    </div>
                    {canBuy && bulkQty > 0 && bulkAmount > 0 && (
                      <div className="text-[10px] uppercase tracking-widest text-[#FFB020]" data-testid={`landing-bolton-bulk-${b.kind || "x"}`}>
                        or {bulkQty} for {money(bulkAmount, b.currency)}
                      </div>
                    )}
                    {key && (
                      <button
                        type="button"
                        onClick={() => { basket.addItem(key); toast.success(`${b.label} added`); }}
                        disabled={locked}
                        data-testid={`landing-bolton-add-${key}`}
                        className="mt-1 text-[10px] uppercase tracking-wider text-[#CCFF00] hover:underline inline-flex items-center gap-1 disabled:text-[#525252] disabled:no-underline disabled:cursor-not-allowed"
                      >
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    )}
                  </div>
                </div>
                {role && (
                  <p className="text-xs text-[#A3A3A3] leading-relaxed" data-testid={`landing-role-desc-${b.kind === "self_service" ? key : b.kind}`}>{role.description}</p>
                )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-24 text-center">
        <div className="card-surface p-8">
          <h3 className="h-display text-2xl mb-3">Not sure which tier fits?</h3>
          <p className="text-sm text-[#A3A3A3] mb-6">
            Try the whole platform on a 5-minute demo — no card required. Draft a festival, spin up the wall viewer, then pick a tier when you're ready to go live.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Link
              to={user ? "/festival/new" : "/register?as=organiser"}
              data-testid="landing-cta-demo"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-bold"
              style={{ background: "#CCFF00", color: "#0A0A0A" }}
            >
              <Zap className="w-4 h-4" /> Try 5-min demo
            </Link>
            <a
              href="mailto:hello@rally-lab.com?subject=Festival%20Live%20Ops%20enquiry"
              data-testid="landing-cta-contact"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider border border-[#525252] hover:border-white"
            >
              <Mail className="w-4 h-4" /> Contact us for more info
            </a>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#141414] py-8 text-center text-xs text-[#525252]">
        Rally-Lab · Festival Live Ops · built for pickleball and squash festivals
      </footer>
      {/* Iter470 · Fix 1 — The basket panel was declared in state and
          imported at the top, but never actually mounted into the tree.
          Without this, `setBasketOpen(true)` from the Iter467/468 auth
          round-trip had no component to render, so the panel never
          appeared after login. */}
      <FestivalBasketPanel
        pricing={pricing}
        basket={basket}
        open={basketOpen}
        onClose={() => setBasketOpen(false)}
      />
      <ReplaceTierDialog
        pending={pendingTier}
        currentLabel={pricing?.[currentTierKey]?.label}
        newLabel={pricing?.[pendingTier]?.label}
        onCancel={() => setPendingTier(null)}
        onConfirm={() => {
          basket.replaceTier(pendingTier);
          toast.success(`Tier replaced with ${pricing?.[pendingTier]?.label || pendingTier}`);
          setPendingTier(null);
        }}
      />
    </div>
  );
}

function TierCard({ tier, meta, passes, onAddToBasket }) {
  const oneDay = passes.pass_1day;
  const twoDay = passes.pass_2day;
  const threeDay = passes.pass_3day;

  const seatLabel = (n) =>
    n === null || n === undefined
      ? "Talk to us"
      : n === 0
      ? "—"
      : `${n}`;

  const rows = [
    { key: "1-day", pass: oneDay, passKey: `${tier}_1day` },
    { key: "2-day", pass: twoDay, passKey: `${tier}_2day` },
    { key: "3-day", pass: threeDay, passKey: `${tier}_3day` },
  ];

  return (
    <div
      className="card-surface p-5 flex flex-col relative"
      style={meta.highlight ? { borderColor: meta.accent } : undefined}
      data-testid={`landing-tier-${tier}`}
    >
      {meta.highlight && (
        <div className="absolute -top-3 left-4 px-2 py-0.5 text-[9px] uppercase tracking-widest font-bold" style={{ background: meta.accent, color: "#0A0A0A" }}>
          Most popular
        </div>
      )}
      <div className="label-mono" style={{ color: meta.accent }}>{meta.label.toUpperCase()}</div>
      <div className="text-xs text-[#A3A3A3] mt-1">{meta.tagline}</div>

      {/* Price rows */}
      <div className="mt-4 space-y-2">
        {rows.map(({ key, pass, passKey }) => {
          const isPoa = pass?.poa || !(Number(pass?.amount) > 0);
          return (
            <div key={key} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-[#A3A3A3]">{key}</span>
              <div className="flex items-center gap-2">
                {isPoa ? (
                  <span className="text-[11px] uppercase tracking-widest text-white font-semibold border border-[#525252] px-2 py-0.5" data-testid={`landing-tier-${tier}-${key}-poa`}>POA</span>
                ) : (
                  <>
                    <span className="text-lg font-bold text-white" data-testid={`landing-tier-${tier}-${key}-price`}>{money(pass.amount, pass.currency)}</span>
                    {onAddToBasket && (
                      <button
                        type="button"
                        onClick={() => onAddToBasket(passKey)}
                        data-testid={`landing-tier-${tier}-${key}-add`}
                        className="w-6 h-6 flex items-center justify-center border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black"
                        aria-label={`Add ${key} to basket`}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Seats */}
      <div className="mt-5 pt-4 border-t border-[#262626] space-y-1.5 text-[11px] text-[#A3A3A3]">
        <SeatRow label="Check-In Helper" value={seatLabel(oneDay?.helper_seats)} />
        <SeatRow label="Court Operations" value={oneDay?.ops_seats === 0 ? "0" : seatLabel(oneDay?.ops_seats)} />
        <SeatRow label="OPS Assist" value={seatLabel(oneDay?.ops_assist)} />
        <SeatRow label="Deputy Admin" value={seatLabel(oneDay?.deputy_seats)} />
        <SeatRow
          label="Self-service"
          value={
            oneDay?.self_service_included
              ? <span className="text-[#CCFF00]">Included</span>
              : meta.self_service_bolton
              ? <span className="text-[#FFB020]">Bolt-on</span>
              : <span className="text-[#525252]">—</span>
          }
        />
      </div>

      {/* CTA */}
      <div className="mt-5 pt-4">
        {meta.manual_only ? (
          <a
            href="mailto:hello@rally-lab.com?subject=Rally-Lab%20Festival%20Unlimited%20tier"
            data-testid={`landing-tier-${tier}-cta`}
            className="w-full block text-center px-4 py-2 text-[11px] uppercase tracking-wider border border-[#525252] hover:border-white"
          >
            Contact us
          </a>
        ) : (
          <Link
            to="/register?as=organiser"
            data-testid={`landing-tier-${tier}-cta`}
            className="w-full block text-center px-4 py-2 text-[11px] uppercase tracking-wider font-bold"
            style={{ background: meta.accent, color: "#0A0A0A" }}
          >
            Get started with {meta.label} →
          </Link>
        )}
      </div>
    </div>
  );
}

function SeatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="uppercase tracking-widest text-[10px]">{label}</span>
      <span className="text-white font-semibold">{value}</span>
    </div>
  );
}

/* Iter306 — Admin custom tier tools. */
function CustomTierList({ pricing, onDeleted }) {
  const customs = Object.entries(pricing || {}).filter(([, def]) => def && def.custom);
  const del = async (passType) => {
    if (!window.confirm(`Delete custom tier "${passType}"?`)) return;
    try {
      const r = await api.delete(`/festival/pricing/custom/${passType}`);
      onDeleted(r.data.pricing);
      toast.success("Custom tier removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };
  if (customs.length === 0) {
    return <div className="text-[10px] uppercase tracking-widest text-[#525252]">No custom tiers yet. Click Add tier to create one.</div>;
  }
  return (
    <div className="grid gap-2 sm:grid-cols-2" data-testid="admin-custom-tier-list">
      {customs.map(([key, def]) => (
        <div key={key} className="border border-[#262626] p-3 flex items-center justify-between gap-3" data-testid={`admin-custom-tier-${key}`}>
          <div className="min-w-0">
            <div className="text-sm text-white truncate">{def.label}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#525252]">
              {def.tier} · {def.days}d · {def.poa ? "POA" : money(def.amount, def.currency)} · cap {def.player_cap ?? "—"}
            </div>
          </div>
          <button type="button" onClick={() => del(key)} data-testid={`admin-custom-tier-delete-${key}`} className="text-[#A3A3A3] hover:text-[#FF4500] p-1"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function AddCustomTierModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    pass_type: "", label: "", tier: "custom", days: 1, amount: 0, currency: "gbp",
    player_cap: "", helper_seats: 0, ops_seats: 0, deputy_seats: 0,
    self_service_included: false, poa: false,
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[a-z0-9_]{3,40}$/.test(form.pass_type)) {
      toast.error("Pass key must be lowercase letters/numbers/underscore (3-40 chars)");
      return;
    }
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      const r = await api.post("/festival/pricing/custom", {
        ...form, amount: Number(form.amount) || 0, days: Number(form.days) || 0,
        player_cap: form.player_cap === "" ? null : Number(form.player_cap),
        helper_seats: Number(form.helper_seats) || 0,
        ops_seats: Number(form.ops_seats) || 0,
        deputy_seats: Number(form.deputy_seats) || 0,
      });
      onCreated(r.data.pricing);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setSaving(false); }
  };
  const U = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="admin-add-tier-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0A0A0A] border border-[#CCFF00] text-white max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Admin</div>
            <div className="text-lg font-bold">Add custom tier</div>
          </div>
          <button type="button" onClick={onClose} className="text-[#A3A3A3] hover:text-white" aria-label="Close">✕</button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="col-span-2 text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Pass key <span className="text-[#525252]">(e.g. weekend_pro)</span>
            <input value={form.pass_type} onChange={(e) => U("pass_type")(e.target.value)} data-testid="add-tier-pass-type" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" maxLength={40} />
          </label>
          <label className="col-span-2 text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Label
            <input value={form.label} onChange={(e) => U("label")(e.target.value)} data-testid="add-tier-label" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" maxLength={80} />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Tier group
            <input value={form.tier} onChange={(e) => U("tier")(e.target.value)} data-testid="add-tier-group" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Days
            <input type="number" min={0} max={14} value={form.days} onChange={(e) => U("days")(e.target.value)} data-testid="add-tier-days" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Amount (£)
            <input type="number" min={0} step="0.01" value={form.amount} onChange={(e) => U("amount")(e.target.value)} disabled={form.poa} data-testid="add-tier-amount" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00] disabled:opacity-40" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Player cap
            <input type="number" min={0} value={form.player_cap} placeholder="blank = unlimited" onChange={(e) => U("player_cap")(e.target.value)} data-testid="add-tier-cap" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Helper seats
            <input type="number" min={0} value={form.helper_seats} onChange={(e) => U("helper_seats")(e.target.value)} data-testid="add-tier-helper" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            OPS seats
            <input type="number" min={0} value={form.ops_seats} onChange={(e) => U("ops_seats")(e.target.value)} data-testid="add-tier-ops" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
            Deputy seats
            <input type="number" min={0} value={form.deputy_seats} onChange={(e) => U("deputy_seats")(e.target.value)} data-testid="add-tier-deputy" className="w-full mt-1 bg-transparent border border-[#262626] px-2 py-1.5 text-sm text-white focus:outline-none focus:border-[#CCFF00]" />
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={form.self_service_included} onChange={(e) => U("self_service_included")(e.target.checked)} data-testid="add-tier-ss" />
            Include self-service
          </label>
          <label className="text-[10px] uppercase tracking-widest text-[#A3A3A3] col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={form.poa} onChange={(e) => U("poa")(e.target.checked)} data-testid="add-tier-poa" />
            POA (Price on Application — no self-serve checkout)
          </label>
        </div>
        <div className="px-5 pb-5">
          <button type="submit" disabled={saving} data-testid="add-tier-submit" className="w-full px-4 py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-40" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
            {saving ? "Saving…" : "Create tier"}
          </button>
        </div>
      </form>
    </div>
  );
}
